import React, { useEffect, useMemo, useRef, useState } from 'react';
import OrbitalVisualization from './OrbitalVisualization';
import { FAKE_LOGS } from '../utils/fakeLogs';
import '../styles/terminal.css';

/**
 * Production-quality terminal loader with 3D orbital visualization
 * @param {{ onConnect: () => void }} props
 */
export default function TerminalLoader({ onConnect }) {
  // Detect reduced motion preference for accessibility
  const reducedMotion = useMemo(
    () => typeof window !== 'undefined' && 
          window.matchMedia && 
          window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  // Typewriter and logging state
  const [renderedLines, setRenderedLines] = useState([]); // array of strings or objects
  const [isDone, setIsDone] = useState(false);
  const [progress, setProgress] = useState(0);

  const scrollRef = useRef(null);
  const skipRef = useRef(false);

  // Main typewriter effect and logging sequence
  useEffect(() => {
    let mounted = true;

    const typeLine = async (line, speedPerChar = 8) => {
      if (skipRef.current || reducedMotion) {
        return line.text; // instant for reduced motion or skip
      }
      
      const chars = line.text.split('');
      let output = '';
      
      for (let i = 0; i < chars.length; i++) {
        if (!mounted) return output;
        if (skipRef.current) { 
          output = line.text; 
          break; 
        }
        
        output += chars[i];
        const currentOutput = output; // Capture current state to avoid closure issue
        
        // Update the last line incrementally for typewriter effect
        setRenderedLines((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = currentOutput;
          return copy;
        });
        
        // Ultra-fast typing with minimal jitter
        const jitter = Math.max(1, Math.min(3, speedPerChar + (Math.random() * 1)));
        await new Promise(resolve => setTimeout(resolve, jitter));
      }
      
      return output;
    };

    const runLogSequence = async () => {
      const totalLogs = FAKE_LOGS.length;
      
      for (let i = 0; i < totalLogs; i++) {
        if (!mounted) return;
        
        const logEntry = FAKE_LOGS[i];
        
        if (logEntry.instant) {
          // Instant lines appear immediately (like cached/pre-loaded data)
          setRenderedLines((prev) => [...prev, { text: logEntry.text, level: logEntry.level || inferLogLevel(logEntry.text) }]);
        } else {
          // Typed lines use typewriter effect for dramatic moments
          const typingSpeed = typeof logEntry.delay === 'number' ? 
            Math.min(logEntry.delay, 8) : // Cap custom delays at 8ms
            (reducedMotion ? 0 : 8);
          
          // Add empty line placeholder for typewriter effect
          setRenderedLines((prev) => [...prev, '']);
          
          // Type out the line character by character
          await typeLine(logEntry, typingSpeed);
          
          if (!mounted) return;
          
          // Determine log level for styling
          const logLevel = logEntry.level || inferLogLevel(logEntry.text);
          
          // Replace placeholder with final styled line
          setRenderedLines((prev) => {
            const copy = [...prev];
            copy[copy.length - 1] = { text: logEntry.text, level: logLevel };
            return copy;
          });
        }
        

        // Update progress bar
        setProgress(Math.round(((i + 1) / totalLogs) * 100));

        // Much faster pauses for sub-1-second loading
        const pauseDuration = reducedMotion ? 5 : 
          (logEntry.pauseAfter ? Math.min(logEntry.pauseAfter, 25) : 
            (logEntry.instant ? (2 + Math.random() * 3) : (5 + Math.random() * 5)));
        await new Promise(resolve => setTimeout(resolve, pauseDuration));
        
        // Auto-scroll to bottom
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
        
        if (skipRef.current) break;
      }
      
      setProgress(100);
      setIsDone(true);
      
      // Ensure final scroll to bottom when button appears
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 100); // Small delay to ensure DOM has updated
    };

    runLogSequence();

    return () => { 
      mounted = false; 
    };
  }, [reducedMotion]);

  // Infer log level from text content for styling
  const inferLogLevel = (text) => {
    if (/err|fail|denied|error/i.test(text)) return 'err';
    if (/warn|deprecated|caution/i.test(text)) return 'warn';
    if (/ok|\bready\b|established|signature ok|success/i.test(text)) return 'ok';
    return 'info';
  };

  // Keyboard controls: Enter to connect when done, S to skip logs
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 's' || event.key === 'S') {
        if (!isDone) {
          skipRef.current = true;
          // Fast-forward all logs instantly
          setRenderedLines(
            FAKE_LOGS.map(log => ({ 
              text: log.text, 
              level: log.level || inferLogLevel(log.text) 
            }))
          );
          setProgress(100);
          setIsDone(true);
          
          // Ensure scroll to bottom when skipping
          setTimeout(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
          }, 100);
        }
      } else if (event.key === 'Enter') {
        if (isDone && onConnect) {
          onConnect();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDone, onConnect]);

  // Ensure scroll to bottom when button appears
  useEffect(() => {
    if (isDone && scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 150); // Slightly longer delay to ensure button has rendered
    }
  }, [isDone]);

  return (
    <div className="terminal-root" role="application" aria-label="Satellite uplink terminal loader">
      {/* Sci-fi spy hardware background */}
      <div className="hardware-background">
        <div className="circuit-pattern"></div>
        <div className="data-streams">
          <div className="data-stream stream-1"></div>
          <div className="data-stream stream-2"></div>
          <div className="data-stream stream-3"></div>
        </div>
        {/* Corner hardware elements */}
        <div className="corner-elements">
          <div className="corner-bracket top-left"></div>
          <div className="corner-bracket top-right"></div>
          <div className="corner-bracket bottom-left"></div>
          <div className="corner-bracket bottom-right"></div>
        </div>
      </div>
      
      {/* Main title */}
      <div className="main-title">
        <h1>SOLPORT STUDIOS SATELLITE UPLINK SPL-001</h1>
      </div>
      
      <div className="terminal-wrap">
        {/* Left panel: 3D Orbital Visualization */}
        <section className="panel" aria-label="Orbital visualization">
          <h3 className="panel-title">
            SATELLITE TRACKING<span className="blinking-dots">...</span>
          </h3>
          <OrbitalVisualization />
        </section>

        {/* Right panel: Terminal Logs */}
        <section className="panel" aria-label="Compile & uplink logs">
          <h3 className="panel-title">UPLINK CONSOLE</h3>
          
          {/* Skip hint */}
          {!isDone && (
            <div className="hint">
              Press <strong>S</strong> to skip
            </div>
          )}
          
          {/* Scrollable log area */}
          <div className="logs">
            <div ref={scrollRef} className="log-scroll" aria-live="polite">
              {renderedLines.map((line, index) => {
                // Handle typewriter in-progress lines (strings)
                if (typeof line === 'string') {
                  return (
                    <pre key={index} className="log-line">
                      {line}<span className="caret" />
                    </pre>
                  );
                }
                
                // Handle completed lines (objects with level)
                return (
                  <pre key={index} className={`log-line ${line.level || 'info'}`}>
                    {line.text}
                  </pre>
                );
              })}
            </div>

            {/* Progress bar */}
            <div className="progress" aria-label={`Progress ${progress}%`}>
              <span style={{ width: `${progress}%` }} />
            </div>

            {/* Progress percentage */}
            <div className="percent">{progress}%</div>

            {/* Enhanced CTA area - always visible when ready */}
            {isDone && (
              <div className="cta-area">
                <div className="cta-container">
                  <button
                    className="cta enhanced-cta"
                    onClick={() => onConnect && onConnect()}
                    aria-label="Connect to satellite"
                  >
                    <span className="cta-main-text">CONNECT TO SATELLITE</span>
                    <span className="cta-subtext">Connect to Satellite to view launched projects.</span>
                  </button>
                  <div className="cta-note-external">
                    Please note: Experience optimized for desktop.
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      
      {/* Footer with social and documentation links */}
      <footer className="terminal-footer">
        <div className="footer-content">
          <a 
            href="https://x.com/solportfun" 
            target="_blank" 
            rel="noopener noreferrer"
            className="footer-link"
            aria-label="Follow us on X"
          >
            <div className="link-icon">
              <svg viewBox="0 0 24 24" fill="currentColor" className="icon">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </div>
            <span className="link-text">@solportfun</span>
          </a>
          
          <div className="footer-separator">|</div>
          
          <a 
            href="/docs" 
            className="footer-link"
            aria-label="View Documentation"
          >
            <div className="link-icon">
              <svg viewBox="0 0 24 24" fill="currentColor" className="icon">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
              </svg>
            </div>
            <span className="link-text">Documentation</span>
          </a>
        </div>
      </footer>
    </div>
  );
}
